import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import type { AxiosError } from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useCartStore } from '../../stores/cartStore';
import { useCreateReservation } from '../../hooks/useReservation';
import { useAuthStore } from '../../stores/authStore';
import { useCartTax } from '../../hooks/useTaxRate';
import { formatTaxRatePercent } from '../../config/pricing';

interface CheckoutFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  notes?: string;
}

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [reservationId, setReservationId] = useState<string | null>(null);
  const [trackingNumber, setTrackingNumber] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { user, isAuthenticated } = useAuthStore();
  const items = useCartStore((state) => state.items);
  // Per-item, per-organization — see useCartTax. Must agree with CartPage.
  const { amount: tax, uniformRate } = useCartTax(items);
  const getTotal = useCartStore((state) => state.getTotal());
  const getItemsByLocation = useCartStore((state) => state.getItemsByLocation);
  const hasPreorderItems = useCartStore((state) => state.hasPreorderItems);
  const clearCart = useCartStore((state) => state.clearCart);
  const discountRate = useCartStore((state) => state.discountRate);
  const discountCode = useCartStore((state) => state.discountCode);

  const itemsByLocation = getItemsByLocation();
  const hasPreorders = hasPreorderItems();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CheckoutFormData>();

  // Autofill form when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      reset({
        customerName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        customerEmail: user.email || '',
        customerPhone: user.phone || '',
        notes: '',
      });
    }
  }, [isAuthenticated, user, reset]);

  const { mutate: createReservation, isPending } = useCreateReservation();

  if (items.length === 0 && !reservationId) {
    return (
      <Container maxWidth="lg">
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Your cart is empty
          </Typography>
          <Button variant="contained" onClick={() => navigate('/products')}>
            Browse Products
          </Button>
        </Paper>
      </Container>
    );
  }

  const onSubmit = (data: CheckoutFormData) => {
    createReservation(
      {
        customerName: data.customerName,
        customerEmail: data.customerEmail,
        customerPhone: data.customerPhone,
        notes: data.notes,
        items: items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
      },
      {
        onSuccess: (response) => {
          setReservationId(response.id);
          setTrackingNumber(response.trackingNumber);
          setActiveStep(1);
          clearCart();
        },
        onError: (error: Error) => {
          const axiosError = error as AxiosError<{ message?: string }>;
          setSnackbar({
            open: true,
            message: axiosError.response?.data?.message || 'Something went wrong. Please try again.',
            severity: 'error',
          });
        },
      }
    );
  };

  const steps = ['Customer Information', 'Confirmation'];

  return (
    <Container maxWidth="md">
      <Typography variant="h3" gutterBottom fontWeight="bold">
        Checkout
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Grid container spacing={3}>
          {/* Customer Form */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Your Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <TextField
                  {...register('customerName', {
                    required: 'Name is required',
                  })}
                  label="Full Name"
                  fullWidth
                  error={!!errors.customerName}
                  helperText={errors.customerName?.message}
                />

                <TextField
                  {...register('customerEmail', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.customerEmail}
                  helperText={errors.customerEmail?.message}
                />

                <TextField
                  {...register('customerPhone', {
                    required: 'Phone number is required',
                  })}
                  label="Phone Number"
                  fullWidth
                  error={!!errors.customerPhone}
                  helperText={errors.customerPhone?.message}
                />

                <TextField
                  {...register('notes')}
                  label="Notes (Optional)"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Any special requests or notes for the store..."
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isPending}
                  startIcon={isPending && <CircularProgress size={20} />}
                >
                  {isPending ? 'Processing...' : 'Complete Reservation'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Order Summary */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Order Summary
              </Typography>
              <Divider sx={{ my: 2 }} />

              {Array.from(itemsByLocation.entries()).map(([locationId, locationItems]) => (
                <Box key={locationId} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {locationItems[0].locationName}
                  </Typography>
                  {locationItems.map((item) => (
                    <Box
                      key={item.productId}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {item.name} x{item.quantity}
                        </Typography>
                        {item.isPreorder && (
                          <Chip
                            icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                            label="Pre-order"
                            size="small"
                            color="warning"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2">
                        {(item.price * item.quantity).toFixed(2)} DZD
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                </Box>
              ))}

              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography>Subtotal</Typography>
                  <Typography>{getTotal.toFixed(2)} DZD</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography>
                    {uniformRate !== null ? `Tax (${formatTaxRatePercent(uniformRate)}%)` : 'Tax'}
                  </Typography>
                  <Typography>{tax.toFixed(2)} DZD</Typography>
                </Box>
                {discountCode !== null && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography>Discount</Typography>
                    <Typography color="success.main">
                      -{(getTotal * discountRate).toFixed(2)} DZD
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {(getTotal + tax - getTotal * discountRate).toFixed(2)} DZD
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Alert severity="info" sx={{ mt: 2 }}>
              You will receive a confirmation email with pickup instructions for each
              store.
            </Alert>

            {hasPreorders && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<ScheduleIcon />}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Pre-order Items
                </Typography>
                <Typography variant="body2">
                  Some items in your cart are pre-orders and will ship when available.
                  You'll be notified when they're ready for pickup.
                </Typography>
              </Alert>
            )}
          </Grid>
        </Grid>
      )}

      {activeStep === 1 && reservationId && trackingNumber && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CheckCircleIcon
            sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
          />
          <Typography variant="h3" gutterBottom fontWeight="bold">
            Reservation Complete!
          </Typography>

          <Box sx={{ my: 3, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Your Tracking Number
            </Typography>
            <Typography variant="h4" color="primary" fontWeight="bold" sx={{ letterSpacing: 2 }}>
              {trackingNumber}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2" gutterBottom>
              <strong>Important:</strong> Save this tracking number to monitor your reservation status.
            </Typography>
            <Typography variant="body2">
              You can track your reservation anytime using this number and your email.
            </Typography>
          </Alert>

          <Typography variant="body1" paragraph>
            We've sent a confirmation email with pickup instructions. Each store
            will prepare your items for pickup.
          </Typography>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => navigate('/')}>
              Back to Home
            </Button>
            <Button variant="outlined" onClick={() => navigate('/track-reservation')}>
              Track Reservation
            </Button>
            <Button variant="outlined" onClick={() => navigate('/products')}>
              Continue Shopping
            </Button>
          </Box>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
